import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DISEASE_OPTIONS, normalizeDisease, diagnosisLabel } from "../constants/diseaseOptions";
import BackupCrcInput from "./study/BackupCrcInput";
import LeadCrcInput from "./study/LeadCrcInput";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";
import { normalizeBackupCrcs } from "../services/backupCrc";
import {
  X,
  Save,
  Plus,
  FileText,
  Activity,
  ChevronDown,
  Sparkles,
  CalendarDays,
} from "lucide-react";

const AddStudyModal = ({ onSave, onClose }) => {
  const initialState = {
    STUDY_ID: "",
    SPONSOR: "",
    STATUS: "",
    POD: "",
    SITE_ID: "",
    LEAD_CRC: "",
    BUDGET_ACCEPTED: "",
    SUBJECT_PAYMENT_METHOD: "",
    TRAVEL: "",
    ENROLLED: "",
    GOAL: "",
    DISEASE: "",
    CDA: "",
    FEASIBILITY: "",
    SQV_DATE: "",
    SSC: "",
    IRB_SUBMISSION: "",
    SIV: "",
    ISF_SETUP: "",
    FDF: "",
    CREDENTIALS: "",
    PSP: "",
    IB_AOR: "",
    SCF: "",
    TL: "",
    DOA: "",
    SIV_FOLLOWUP: "",
    SDA: "",
    SRP: "",
    COI: "",
    GREENLIGHT: "",
    COV: ""
  };

  const [formData, setFormData] = useState(initialState);
  // Backup CRCs: edited as rows, saved as the BACKUP_CRC list.
  const [backupRows, setBackupRows] = useState([""]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
  const data = { ...formData };

  if (data.DISEASE) {
    data.DISEASE = normalizeDisease(data.DISEASE);
  }
  const backups = normalizeBackupCrcs(backupRows);
  if (backups.length) data.BACKUP_CRC = backups;

  onSave(data);
  onClose();
};

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const formatLabel = (k) => k.replace(/_/g, " ");

  // Visually group fields without altering data shape or submission
  const coreKeys = [
    "STUDY_ID",
    "SPONSOR",
    "STATUS",
    "POD",
    "SITE_ID",
    "LEAD_CRC",
    "BACKUP_CRC",
    "BUDGET_ACCEPTED",
    "SUBJECT_PAYMENT_METHOD",
    "TRAVEL",
    "ENROLLED",
    "GOAL",
  ];
  const milestoneKeys = [
    "CDA",
    "FEASIBILITY",
    "SQV_DATE",
    "SSC",
    "IRB_SUBMISSION",
    "SIV",
    "ISF_SETUP",
    "FDF",
    "CREDENTIALS",
    "PSP",
    "IB_AOR",
    "SCF",
    "TL",
    "DOA",
    "SIV_FOLLOWUP",
    "SDA",
    "SRP",
    "COI",
    "GREENLIGHT",
    "COV",
  ];

  const renderField = (key) =>
    key === "BACKUP_CRC" ? (
      <BackupCrcInput key={key} rows={backupRows} onChange={setBackupRows} label="BACKUP CRC" />
    ) : key === "LEAD_CRC" && UI_REVAMP_ENABLED ? (
      // UI revamp: picked from the roster, like Backup CRC (still a string).
      <LeadCrcInput
        key={key}
        value={formData.LEAD_CRC}
        onChange={(name) => setFormData((prev) => ({ ...prev, LEAD_CRC: name }))}
        label="LEAD CRC"
      />
    ) : (
    <label key={key} className="block">
      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
        {formatLabel(key)}
      </span>
      <input
        type="text"
        name={key}
        value={formData[key]}
        onChange={handleChange}
        className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
      />
    </label>
  );

  return (
    <AnimatePresence>
      <motion.div
        onMouseDown={handleBackdropClick}
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
      <motion.div
        onMouseDown={(e) => e.stopPropagation()}
        initial={{ y: 18, scale: 0.98, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 18, scale: 0.98, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] flex flex-col text-slate-100"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-study-title"
      >
        {/* Top accent line */}
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-purple-600/20 blur-[120px]" />
          <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-600/20 blur-[120px]" />
        </div>

        {/* Header */}
        <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
          <div className="relative shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
            <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
              <Plus className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
              Operations
            </div>
            <h2
              id="add-study-title"
              className="text-base font-semibold tracking-tight text-white leading-tight"
            >
              Add New Study
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="relative overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {/* Disease */}
          <Section
            title="Disease"
            icon={<Activity className="w-3.5 h-3.5 text-purple-300" />}
          >
            <div className="relative">
              <select
                name="DISEASE"
                value={formData.DISEASE}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    DISEASE: normalizeDisease(e.target.value),
                  })
                }
                className="w-full appearance-none rounded-xl bg-white/[0.04] border border-white/10 pl-3 pr-9 py-2 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition"
              >
                <option value="" className="bg-[#0b0820] text-slate-100">
                  Select disease
                </option>
                {DISEASE_OPTIONS.map((d) => (
                  <option key={d} value={d} className="bg-[#0b0820] text-slate-100">
                    {diagnosisLabel(d)}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            </div>
          </Section>

          {/* Core info */}
          <Section
            title="Core Information"
            icon={<FileText className="w-3.5 h-3.5 text-purple-300" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {coreKeys.map(renderField)}
            </div>
          </Section>

          {/* Milestones */}
          <Section
            title="Milestones & Regulatory"
            icon={<CalendarDays className="w-3.5 h-3.5 text-purple-300" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {milestoneKeys.map(renderField)}
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 backdrop-blur-xl flex items-center justify-end gap-2 shrink-0">
          <div className="hidden sm:flex items-center gap-1.5 mr-auto text-[10px] uppercase tracking-wider text-slate-400">
            <Sparkles className="w-3 h-3 text-purple-300" />
            New study draft
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm font-medium text-slate-200 hover:text-white transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <Save className="w-4 h-4 relative" />
            <span className="relative">Save</span>
          </button>
        </div>
      </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Section = ({ title, icon, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-xl p-4 relative overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <span className="text-[10px] uppercase tracking-[0.22em] text-slate-300 font-semibold">
        {title}
      </span>
      <span className="ml-2 h-px flex-1 bg-white/5" />
    </div>
    {children}
  </div>
);

export default AddStudyModal;
